// React
import React, { useState, useEffect, useMemo } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import Table from "../../../../components/ReactTable/Table";
import TextFilter from "../../../../components/ReactTable/Filters/TextFilter";
import { useInstructors } from "../../../../hooks/manageInstructorsLocations";
import { useDispatch } from "react-redux";
import { enqueueErrorSnackbar } from "../../../../redux/actions/snackbarActions";
import StatusFilter from "./StatusFilter";
import { courseStatuses } from "../../../../constants/index";
import EditInstructorModal from "./EditInstructorModal";
import { useTableData } from "../../../../components/ReactTable/useTableData";
import { enqueueSuccessSnackbar } from "../../../../redux/actions/snackbarActions";
import { getLocationString } from "../../../../utils/string";
import { instructorTypes } from "../../../../constants/index";
import AddInstructorButton from "./AddInstructorButton";

const columns = t => [
  {
    Header: t("table.columnHeader.name"),
    accessor: "fullName",
    id: "fullName",
    canSort: true,
    Filter: TextFilter,
    filterPlaceholder: t("table.filterPlaceholder.nameAll")
  },
  {
    Header: t("table.columnHeader.location"),
    accessor: ({ city, state }) => getLocationString(city, state),
    id: "location",
    canSort: true
  },
  {
    Header: t("table.columnHeader.type"),
    accessor: ({ isExternal }) =>
      t("manageCourses.type", {
        context: isExternal ? "external" : "internal"
      }),
    id: "isExternal",
    canSort: true
  },
  {
    Header: t("table.columnHeader.Status"),
    accessor: "status",
    id: "status",
    canSort: true,
    cellStyle: {
      minWidth: 100,
      textTransform: "capitalize"
    }
  }
];

const Instructors = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const memoColumns = useMemo(() => columns(t), []);

  const [instructorType, setInstructorType] = useState(
    instructorTypes.internal
  );
  const [selectedInstructor, setSelectedInstructor] = useState(null);
  const [editInstructorModalOpen, setEditInstructorModalOpen] = useState(false);

  useEffect(() => {
    setInstructorType(
      selectedInstructor?.isExternal
        ? instructorTypes.external
        : instructorTypes.internal
    );
  }, [selectedInstructor]);

  const { getInstructors, updateInstructor } = useInstructors();
  const {
    data,
    isFetching,
    refreshData,
    paginationState,
    onStateChange,
    onApplyExternalFilters
  } = useTableData(getInstructors, {
    externalFilters: { statusTypeId: courseStatuses[1].id }
  });

  const handleFilter = newStatusFilter => {
    onApplyExternalFilters({ statusTypeId: newStatusFilter });
  };

  const handleUpdateInstructor = instructorData => {
    return updateInstructor(selectedInstructor.id, instructorData)
      .then(() => {
        setEditInstructorModalOpen(false);
        setSelectedInstructor(null);
        if (
          parseInt(instructorData.statusType.id) === courseStatuses[2].id &&
          instructorData.instructorType === "internal"
        ) {
          dispatch(
            enqueueSuccessSnackbar({
              message: t(
                "editInstructorModal.successMsgForInactivatingInternal"
              )
            })
          );
        } else {
          dispatch(
            enqueueSuccessSnackbar({
              message: t("editInstructorModal.successMsg")
            })
          );
        }
        refreshData();
      })
      .catch(errors => {
        dispatch(enqueueErrorSnackbar({ message: errors[0].detail }));
      });
  };

  const handleSaveInstructor = instructorData =>
    handleUpdateInstructor(instructorData);

  const handleOpenEditInstructorModal = instructorData => {
    setSelectedInstructor(instructorData);
    setEditInstructorModalOpen(true);
  };

  const handleCloseEditInstructorModal = () => {
    setSelectedInstructor(null);
    setEditInstructorModalOpen(false);
  };

  return (
    <Box display="flex" flexDirection="column" mt={3}>
      <Box display="flex" flexDirection="column">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap-reverse"
        >
          <Box mb={3} mr={3}>
            <StatusFilter
              defaultValue={courseStatuses[1].id}
              onChange={handleFilter}
            />
          </Box>
          <Box mb={3}>
            <AddInstructorButton onSave={() => refreshData()} />
          </Box>
        </Box>
      </Box>
      <Table
        data={data}
        columns={memoColumns}
        onStateChange={onStateChange}
        cellSize="medium"
        paginationState={paginationState}
        paginated={true}
        loading={isFetching}
        onRowClick={({ original }) => handleOpenEditInstructorModal(original)}
      />
      <EditInstructorModal
        open={editInstructorModalOpen}
        existingInstructor={selectedInstructor}
        onClose={handleCloseEditInstructorModal}
        onSave={handleSaveInstructor}
        defaultInstructorType={instructorType}
      />
    </Box>
  );
};

export default Instructors;
